'use client';

import Image from 'next/image';

import { LineDashedHorizontalIcon } from '@/components/icons/svgs';

import { cn } from '@/lib/utils';

import { TRUSTED_STARTUPS_COUNT, TRUSTED_STARTUPS_LOGOS } from '../../constants/landing-constants';

type TrustedStartupsProps = {
	hideDivider?: boolean;
	compact?: boolean;
	/** Extra classes on the section, e.g. more bottom padding when the next section has no top margin. */
	className?: string;
};

export const TrustedStartups = ({
	hideDivider = false,
	compact = false,
	className,
}: TrustedStartupsProps) => {
	const duplicatedLogos = [...TRUSTED_STARTUPS_LOGOS, ...TRUSTED_STARTUPS_LOGOS];
	// Both variants render; CSS picks one at the `sm` breakpoint (the old 640px mobile cutoff),
	// so the server HTML already matches the screen and nothing swaps after hydration.
	const marquee = (
		<div
			className={cn(
				'relative w-full overflow-hidden sm:hidden',
				compact &&
					'[mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]'
			)}
		>
			<div
				className="flex items-center gap-8"
				style={{
					animation: 'scroll-left 28s linear infinite',
					width: 'max-content',
				}}
			>
				{duplicatedLogos.map((logo, index) => (
					<div
						key={`${logo.alt}-${index}`}
						className="flex shrink-0 items-center"
					>
						<Image
							src={logo.src}
							alt={`${logo.alt} logo`}
							width={logo.width}
							height={logo.height}
							sizes="140px"
							className={cn(
								'h-10 w-auto object-contain',
								compact && 'h-9 opacity-60 md:h-10'
							)}
							loading="lazy"
						/>
					</div>
				))}
			</div>
			<style jsx>{`
				@keyframes scroll-left {
					0% {
						transform: translateX(0);
					}
					100% {
						transform: translateX(-50%);
					}
				}
			`}</style>
		</div>
	);
	const row = (
		<div
			className={cn(
				'hidden w-full items-center justify-center gap-8 sm:flex md:gap-12 lg:gap-16',
				compact && 'md:justify-center md:gap-6 lg:gap-8'
			)}
		>
			{TRUSTED_STARTUPS_LOGOS.map((logo) => (
				<div
					key={logo.alt}
					className="flex items-center transition-opacity"
				>
					<Image
						src={logo.src}
						alt={`${logo.alt} logo`}
						width={logo.width}
						height={logo.height}
						sizes="220px"
						className={cn(
							'h-10 w-auto object-contain md:h-16',
							compact && 'h-9 opacity-60 md:h-10'
						)}
						loading="lazy"
					/>
				</div>
			))}
		</div>
	);
	const logos = (
		<>
			{marquee}
			{row}
		</>
	);

	if (compact) {
		return (
			<section
				className={cn(
					'landing-width flex w-full flex-col items-center overflow-hidden pb-8 md:pb-10',
					className
				)}
			>
				<h2 className="mb-3 text-center text-sm font-semibold uppercase tracking-wide text-secondary/85 md:mb-4">
					Trusted by {TRUSTED_STARTUPS_COUNT}+ startups
				</h2>

				<div className="w-full min-w-0 overflow-hidden">{logos}</div>

				{!hideDivider && (
					<div className="mx-auto my-2 w-[94%] overflow-hidden">
						<LineDashedHorizontalIcon className="mb-12 mt-16 md:mt-20" />
					</div>
				)}
			</section>
		);
	}

	return (
		<div
			className={cn(
				'landing-width flex w-full flex-col items-center gap-4 overflow-hidden pb-8 md:gap-6 md:pb-12',
				hideDivider && 'pb-20 md:pb-28',
				className
			)}
		>
			<h2 className="text-center text-base font-medium leading-[160%] tracking-[0%] text-[#636363] text-secondary md:text-left md:text-lg">
				Trusted by {TRUSTED_STARTUPS_COUNT}+ top startups
			</h2>

			{logos}

			{!hideDivider && (
				<div className="mx-auto my-2 w-[94%] overflow-hidden">
					<LineDashedHorizontalIcon className="mb-12 mt-16 md:mt-20" />
				</div>
			)}
		</div>
	);
};
